import { memo, useState, MouseEvent } from 'react'
import { Check, Brain } from 'lucide-react'
import { copyTextToClipboard } from '@renderer/utils/clipboard'
import { useDevMode, useUser } from '@renderer/chat/store'
import { Button, Tooltip, type ButtonVariant } from '@highlight/ui'

interface BraintrustSpanLinkProps {
  link: string
  className?: string
  variant?: ButtonVariant
}

export function isHighlightStaffEmail(email: string | undefined): boolean {
  return email?.trim().toLowerCase().endsWith('@highlight.ing') ?? false
}

/** Staff-only, and only while developer mode is on — nothing here is for everyday use. */
export function useCanViewBraintrustLinks(): boolean {
  const devMode = useDevMode()
  const user = useUser()
  return devMode && isHighlightStaffEmail(user?.email)
}

export const BraintrustSpanLink = memo(function BraintrustSpanLink({
  link,
  className,
  variant = 'ghost',
}: BraintrustSpanLinkProps) {
  const [copiedLink, setCopiedLink] = useState(false)
  const canViewBraintrustLinks = useCanViewBraintrustLinks()

  const handleCopyBraintrustLink = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!link) return
    const success = await copyTextToClipboard(link)
    if (success) {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    }
  }

  if (!canViewBraintrustLinks) {
    return null
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant={variant}
            shape="square"
            aria-label="Copy Braintrust link"
            className={className}
            onClick={handleCopyBraintrustLink}
          >
            {copiedLink ? <Check /> : <Brain />}
          </Button>
        }
      />
      <Tooltip.Content>{copiedLink ? 'Copied!' : 'Copy Braintrust link'}</Tooltip.Content>
    </Tooltip>
  )
})
